import { beforeEach, describe, expect, it, vi } from 'vitest';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { signMandate, SignMandateValidationError, type SignMandateParams } from './sign-mandate';

vi.mock('receivable-invoices-js/react/utils/api-clients/qonto');
vi.mock('receivable-invoices-js/constants/hosts', () => ({
  apiBaseURL: '/api',
}));

interface SignMandateRequestBody {
  data: {
    type: string;
    attributes: {
      client_iban: string;
      client_snapshot: {
        address?: string;
        city?: string;
        zip_code?: string;
        province_code?: string;
        country_code?: string;
      };
    };
  };
}

describe(signMandate, () => {
  const mockQontoClient = vi.mocked(qontoClient);
  const URL = '/api/v1/direct_debit_collection_mandates/shared';

  beforeEach(() => {
    vi.clearAllMocks();
  });

  const baseParams: SignMandateParams = {
    mandateId: 'mandate-123',
    clientIban: 'FR1420041010050500013M02606',
    clientAddress: '18 rue de Navarin',
    clientCity: 'Paris',
    clientZipCode: '75009',
    clientCountryCode: 'FR',
  };

  it('signs mandate successfully with all parameters', async () => {
    mockQontoClient.mockResolvedValue({});

    const result = await signMandate(baseParams);

    expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/mandate-123/sign`, {
      method: 'PATCH',
      body: JSON.stringify({
        data: {
          type: 'direct-debit-collection-mandates',
          attributes: {
            client_iban: 'FR1420041010050500013M02606',
            client_snapshot: {
              address: '18 rue de Navarin',
              city: 'Paris',
              zip_code: '75009',
              country_code: 'FR',
            },
          },
        },
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    expect(result).toBe(true);
  });

  it('signs mandate with only required parameters', async () => {
    mockQontoClient.mockResolvedValue({});

    const minimalParams: SignMandateParams = {
      mandateId: 'mandate-123',
      clientIban: 'FR1420041010050500013M02606',
    };

    const result = await signMandate(minimalParams);

    expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/mandate-123/sign`, {
      method: 'PATCH',
      body: JSON.stringify({
        data: {
          type: 'direct-debit-collection-mandates',
          attributes: {
            client_iban: 'FR1420041010050500013M02606',
            client_snapshot: {},
          },
        },
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    expect(result).toBe(true);
  });

  it('signs mandate with province code for Italy', async () => {
    mockQontoClient.mockResolvedValue({});

    const paramsWithProvince: SignMandateParams = {
      ...baseParams,
      clientCountryCode: 'IT',
      clientProvinceCode: 'MI',
    };

    await signMandate(paramsWithProvince);

    expect(mockQontoClient).toHaveBeenCalledTimes(1);
    const callArgs = mockQontoClient.mock.calls[0];
    expect(callArgs).toBeDefined();
    expect(callArgs?.[1]?.body).toBeDefined();
    const body = JSON.parse(callArgs?.[1]?.body as string) as SignMandateRequestBody;

    expect(callArgs?.[0]).toBe(`${URL}/mandate-123/sign`);
    expect(callArgs?.[1]?.method).toBe('PATCH');
    expect(callArgs?.[1]?.headers).toStrictEqual({
      'Content-Type': 'application/json',
    });
    expect(body).toStrictEqual({
      data: {
        type: 'direct-debit-collection-mandates',
        attributes: {
          client_iban: 'FR1420041010050500013M02606',
          client_snapshot: {
            address: '18 rue de Navarin',
            city: 'Paris',
            zip_code: '75009',
            province_code: 'MI',
            country_code: 'IT',
          },
        },
      },
    });
  });

  it('includes device ID header when provided', async () => {
    mockQontoClient.mockResolvedValue({});

    const paramsWithDeviceId: SignMandateParams = {
      ...baseParams,
      deviceId: 'device-456',
    };

    await signMandate(paramsWithDeviceId);

    expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/mandate-123/sign`, {
      method: 'PATCH',
      body: expect.any(String) as string,
      headers: {
        'Content-Type': 'application/json',
        'X-Qonto-Device': 'device-456',
      },
    });
  });

  describe('validation errors', () => {
    it('throws SignMandateValidationError for invalid IBAN', async () => {
      mockQontoClient.mockRejectedValue({
        status: 422,
        data: {
          errors: [
            {
              code: 'invalid_iban',
              source: { pointer: '/data/attributes/client_iban' },
            },
          ],
        },
      });

      await expect(signMandate(baseParams)).rejects.toThrow(SignMandateValidationError);
      await expect(signMandate(baseParams)).rejects.toMatchObject({
        name: 'SignMandateValidationError',
        errors: [
          {
            code: 'invalid_iban',
            source: { pointer: '/data/attributes/client_iban' },
          },
        ],
      });
    });

    it('throws SignMandateValidationError for SDD disabled', async () => {
      mockQontoClient.mockRejectedValue({
        status: 422,
        data: {
          errors: [
            {
              code: 'sdd_disabled',
              source: { pointer: '/data/attributes/client_iban' },
            },
          ],
        },
      });

      await expect(signMandate(baseParams)).rejects.toThrow(SignMandateValidationError);
      await expect(signMandate(baseParams)).rejects.toMatchObject({
        errors: [
          {
            code: 'sdd_disabled',
            source: { pointer: '/data/attributes/client_iban' },
          },
        ],
      });
    });

    it('throws SignMandateValidationError for invalid zipcode length', async () => {
      mockQontoClient.mockRejectedValue({
        status: 422,
        data: {
          errors: [
            {
              code: 'len',
              source: { pointer: '/data/attributes/client_snapshot/zip_code' },
            },
          ],
        },
      });

      await expect(signMandate(baseParams)).rejects.toThrow(SignMandateValidationError);
    });

    it('throws SignMandateValidationError for required fields', async () => {
      mockQontoClient.mockRejectedValue({
        status: 422,
        data: {
          errors: [
            {
              code: 'required',
              source: { pointer: '/data/attributes/client_snapshot/address' },
            },
            {
              code: 'required',
              source: { pointer: '/data/attributes/client_snapshot/city' },
            },
          ],
        },
      });

      await expect(signMandate(baseParams)).rejects.toThrow(SignMandateValidationError);
      await expect(signMandate(baseParams)).rejects.toMatchObject({
        errors: [
          {
            code: 'required',
            source: { pointer: '/data/attributes/client_snapshot/address' },
          },
          {
            code: 'required',
            source: { pointer: '/data/attributes/client_snapshot/city' },
          },
        ],
      });
    });

    it('throws SignMandateValidationError for mandate not found', async () => {
      mockQontoClient.mockRejectedValue({
        status: 422,
        data: {
          errors: [
            {
              code: 'mandate_not_found',
              source: { pointer: '/data/id' },
            },
          ],
        },
      });

      await expect(signMandate(baseParams)).rejects.toThrow(SignMandateValidationError);
    });

    it('throws SignMandateValidationError for mandate already signed', async () => {
      mockQontoClient.mockRejectedValue({
        status: 422,
        data: {
          errors: [
            {
              code: 'mandate_already_signed',
              source: { pointer: '/data/id' },
            },
          ],
        },
      });

      await expect(signMandate(baseParams)).rejects.toThrow(SignMandateValidationError);
    });
  });

  describe('unknown errors', () => {
    it('re-throws unknown 422 error codes', async () => {
      const unknownError = {
        status: 422,
        data: {
          errors: [
            {
              code: 'unknown_error_code',
              source: { pointer: '/data/attributes/something' },
            },
          ],
        },
      };

      mockQontoClient.mockRejectedValue(unknownError);

      await expect(signMandate(baseParams)).rejects.toStrictEqual(unknownError);
    });

    it('re-throws 500 server errors', async () => {
      const serverError = {
        status: 500,
        message: 'Internal Server Error',
      };

      mockQontoClient.mockRejectedValue(serverError);

      await expect(signMandate(baseParams)).rejects.toStrictEqual(serverError);
    });

    it('re-throws network errors', async () => {
      const networkError = new Error('Network failure');

      mockQontoClient.mockRejectedValue(networkError);

      await expect(signMandate(baseParams)).rejects.toThrow('Network failure');
    });

    it('re-throws malformed error responses', async () => {
      const malformedError = {
        status: 422,
        data: {
          // Missing errors array
          message: 'Something went wrong',
        },
      };

      mockQontoClient.mockRejectedValue(malformedError);

      await expect(signMandate(baseParams)).rejects.toStrictEqual(malformedError);
    });

    it('re-throws error when data is not an object', async () => {
      const invalidError = {
        status: 422,
        data: 'string error',
      };

      mockQontoClient.mockRejectedValue(invalidError);

      await expect(signMandate(baseParams)).rejects.toStrictEqual(invalidError);
    });
  });

  describe('edge cases', () => {
    it('handles empty client snapshot when no optional fields provided', async () => {
      mockQontoClient.mockResolvedValue({});

      const minimalParams: SignMandateParams = {
        mandateId: 'mandate-123',
        clientIban: 'FR1420041010050500013M02606',
      };

      await signMandate(minimalParams);

      expect(mockQontoClient).toHaveBeenCalledTimes(1);
      const callArgs = mockQontoClient.mock.calls[0];
      expect(callArgs).toBeDefined();
      expect(callArgs?.[1]?.body).toBeDefined();
      const body = JSON.parse(callArgs?.[1]?.body as string) as SignMandateRequestBody;

      expect(body.data.attributes.client_snapshot).toStrictEqual({});
    });

    it('omits undefined fields from client snapshot', async () => {
      mockQontoClient.mockResolvedValue({});

      const partialParams: SignMandateParams = {
        mandateId: 'mandate-123',
        clientIban: 'FR1420041010050500013M02606',
        clientAddress: '18 rue de Navarin',
        clientCity: undefined,
        clientZipCode: '75009',
      };

      await signMandate(partialParams);

      expect(mockQontoClient).toHaveBeenCalledTimes(1);
      const callArgs = mockQontoClient.mock.calls[0];
      expect(callArgs).toBeDefined();
      expect(callArgs?.[1]?.body).toBeDefined();
      const body = JSON.parse(callArgs?.[1]?.body as string) as SignMandateRequestBody;

      expect(body.data.attributes.client_snapshot).toStrictEqual({
        address: '18 rue de Navarin',
        zip_code: '75009',
      });
      expect(body.data.attributes.client_snapshot).not.toHaveProperty('city');
    });
  });
});

describe(SignMandateValidationError, () => {
  it('creates error with default message', () => {
    const errors = [
      {
        code: 'invalid_iban',
        source: { pointer: '/data/attributes/client_iban' },
      },
    ];

    const error = new SignMandateValidationError(errors);

    expect(error.name).toBe('SignMandateValidationError');
    expect(error.message).toBe('Mandate signing validation failed');
    expect(error.errors).toStrictEqual(errors);
  });

  it('creates error with custom message', () => {
    const errors = [
      {
        code: 'required',
        source: { pointer: '/data/attributes/client_snapshot/address' },
      },
    ];

    const error = new SignMandateValidationError(errors, 'Custom error message');

    expect(error.message).toBe('Custom error message');
    expect(error.errors).toStrictEqual(errors);
  });

  it('is instance of Error', () => {
    const errors = [
      {
        code: 'sdd_disabled',
        source: { pointer: '/data/attributes/client_iban' },
      },
    ];

    const error = new SignMandateValidationError(errors);

    expect(error).toBeInstanceOf(Error);
    expect(error).toBeInstanceOf(SignMandateValidationError);
  });
});
